import React from 'react';
import { shallow } from 'zustand/shallow';
import { usePromotionStore } from '@/stores/promotion';
import type { BasePromotion } from '@/types/http/promotion/common';
import type { BaseFreeGift } from '@/stores/promotion';
import {
  FreeGiftGroup,
  getMaxQtyForPromo,
  isPromoEligibleFreeGift,
  sumSelectedQtyForPromo,
} from '@/lib/promotions';

type Mode = 'sequence' | 'single' | null;

export function useFreeGiftWizard() {
  const { selectedPromotion, selectedFreeGift, removeFreeGift } = usePromotionStore(
    (s) => ({
      selectedPromotion: s.selectedPromotion,
      selectedFreeGift: s.selectedFreeGift,
      replaceFreeGift: s.replaceFreeGift,
      removeFreeGift: s.removeFreeGift,
    }),
    shallow
  );

  const eligiblePromos = React.useMemo(
    () => (selectedPromotion ?? []).filter(isPromoEligibleFreeGift),
    [selectedPromotion]
  );

  const freeGiftByPromoId = React.useMemo(() => {
    const map = new Map<string, FreeGiftGroup[]>();
    (selectedFreeGift ?? [])
      .filter(
        (g): g is FreeGiftGroup =>
          typeof g.promotion_id === 'string' && g.promotion_id !== undefined
      )
      .forEach((g) => {
        const key = g.promotion_id as string;
        const arr = map.get(key) ?? [];
        arr.push(g);
        map.set(key, arr);
      });
    return map;
  }, [selectedFreeGift]);

  const outstandingPromos = React.useMemo(() => {
    const eligibleIds = new Set(eligiblePromos.map((p) => String(p.promotion_id)));
    const selectedIds = new Set((selectedFreeGift ?? []).map((g) => String(g.promotion_id)));

    if (eligibleIds.size > 0 && Array.from(eligibleIds).every((id) => selectedIds.has(id))) {
      return [];
    }

    return eligiblePromos.filter((p) => {
      const groups = freeGiftByPromoId.get(p.promotion_id) ?? [];

      if (groups.length === 0) return true;

      const taken = sumSelectedQtyForPromo(groups);

      const rawMax = getMaxQtyForPromo(p) ?? 0;
      const hasRewards = Array.isArray(p.product_reward) && p.product_reward.length > 0;

      const maxQty = hasRewards ? Math.max(rawMax, 1) : 0;

      return taken < maxQty;
    });
  }, [eligiblePromos, selectedFreeGift]);

  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<Mode>(null);

  const [flowSteps, setFlowSteps] = React.useState<string[]>([]);
  const [stepIdx, setStepIdx] = React.useState(0);

  const [manualPromoId, setManualPromoId] = React.useState<string | null>(null);

  const currentPromo: BasePromotion | null = React.useMemo(() => {
    if (mode === 'single' && manualPromoId) {
      return eligiblePromos.find((p) => p.promotion_id === manualPromoId) ?? null;
    }
    const pid = flowSteps[stepIdx] ?? '';
    return eligiblePromos.find((p) => p.promotion_id === pid) ?? null;
  }, [mode, manualPromoId, eligiblePromos, flowSteps, stepIdx]);

  const currentPromoId = currentPromo?.promotion_id ?? '';

  const start = (useAllEligible = false) => {
    setMode('sequence');
    setManualPromoId(null);

    const initialSteps = (useAllEligible ? eligiblePromos : outstandingPromos).map(
      (p) => p.promotion_id
    );

    setFlowSteps(initialSteps);
    setStepIdx(0);
    setOpen(initialSteps.length > 0);
  };

  const startEdit = (promoId: string) => {
    setMode('single');
    setManualPromoId(promoId);
    setOpen(true);
  };

  const hasNext = mode === 'sequence' && stepIdx < flowSteps.length - 1;
  const goNext = () => {
    if (mode === 'sequence') setStepIdx((i) => Math.min(i + 1, flowSteps.length - 1));
  };

  const commitOne = (promoId: string, gifts: BaseFreeGift[]) => {
    if (!promoId) return;
    if (!gifts.length) {
      removeFreeGift(promoId);
      return;
    }
    usePromotionStore.getState().replaceFreeGift(promoId, gifts);
  };

  const finish = () => {
    setOpen(false);
    setMode(null);
    setManualPromoId(null);
    setFlowSteps([]);
    setStepIdx(0);
  };

  const initialSelectedForCurrent = React.useMemo(
    () => (selectedFreeGift ?? []).filter((g) => g.promotion_id === currentPromoId),
    [selectedFreeGift, currentPromoId]
  );

  return {
    open,
    setOpen,
    mode,
    flowSteps,
    stepIdx,
    setStepIdx,
    currentPromo,
    currentPromoId,
    start,
    startEdit,
    hasNext,
    goNext,
    finish,
    commitOne,
    initialSelectedForCurrent,
    outstandingPromos,
  };
}
